import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Button,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/Dashboard'
import GroupsIcon from '@mui/icons-material/Groups'
import ComputerIcon from '@mui/icons-material/Computer'
import ScheduleIcon from '@mui/icons-material/Schedule'
import BeachIcon from '@mui/icons-material/BeachAccess'
import ReceiptIcon from '@mui/icons-material/Receipt'
import AssessmentIcon from '@mui/icons-material/Assessment'
import PersonIcon from '@mui/icons-material/Person'
import HomeIcon from '@mui/icons-material/Home'
import EditIcon from '@mui/icons-material/Edit'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import EventIcon from '@mui/icons-material/Event'
import GroupIcon from '@mui/icons-material/Group'
import WorkIcon from '@mui/icons-material/Work'
import { useAuth } from '../contexts/AuthContext'
import { ROLES } from '../constants'
import FirstTimeLoginDialog from './FirstTimeLoginDialog'
import NotificationBadge from './NotificationBadge'
import { usePendingTimeOffCount } from '../hooks/usePendingTimeOffCount'

const drawerWidth = 240
const logo = "/nimbus-tech-logo.png"

const adminMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Welcome', icon: <HomeIcon />, path: '/welcome' },
  { text: 'Our Team', icon: <GroupIcon />, path: '/our-team' },
  { text: 'Upcoming Events', icon: <EventIcon />, path: '/upcoming-events' },
  { text: 'My Notes', icon: <EditIcon />, path: '/notes' },
  { text: 'Employees', icon: <GroupsIcon />, path: '/employees' },
  { text: 'Clients', icon: <WorkIcon />, path: '/clients' },
  { text: 'Projects', icon: <ComputerIcon />, path: '/projects' },
  { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
  { text: 'Timesheets', icon: <ScheduleIcon />, path: '/timesheets' },
  { text: 'Day Off', icon: <BeachIcon />, path: '/time-off' },
  { text: 'Fitpass', icon: <FitnessCenterIcon />, path: '/fitpass' },
  { text: 'Lunch', icon: <RestaurantIcon />, path: '/lunch' },
  { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
  { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
]

const employeeMenuItems = [
  { text: 'Welcome', icon: <HomeIcon />, path: '/welcome' },
  { text: 'Our Team', icon: <GroupIcon />, path: '/our-team' },
  { text: 'Upcoming Events', icon: <EventIcon />, path: '/upcoming-events' },
  { text: 'My Notes', icon: <EditIcon />, path: '/notes' },
  { text: 'Timesheets', icon: <ScheduleIcon />, path: '/timesheets' },
  { text: 'Day Off', icon: <BeachIcon />, path: '/time-off' },
  { text: 'Fitpass', icon: <FitnessCenterIcon />, path: '/fitpass' },
  { text: 'Lunch', icon: <RestaurantIcon />, path: '/lunch' },
  { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
]

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, logout, showFirstTimeLogin, handlePasswordChanged } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { pendingCount } = usePendingTimeOffCount()

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    setMobileOpen(false)
  }

  // Check if a menu item should be highlighted based on current pathname
  const isActive = (pathname: string, itemPath: string) => {
    // Exact match
    if (pathname === itemPath) return true
    // Check if pathname starts with itemPath followed by a slash (for sub-routes)
    // This handles cases like /projects/123/edit matching /projects
    if (pathname.startsWith(itemPath + '/')) return true
    return false
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Toolbar sx={{ flexShrink: 0 }}>
        <img src={logo} alt="Nimbus Tech logo" style={{ width: '100%', height: 'auto' }} />
      </Toolbar>
      <Divider sx={{ flexShrink: 0 }} />
      <List 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          minHeight: 0
        }}
      >
        {(user?.role === ROLES.ADMIN ? adminMenuItems : employeeMenuItems).map((item) => {
          const active = isActive(location.pathname, item.path)
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={active}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit' }}>
                  {item.text === 'Day Off' && user?.role === ROLES.ADMIN ? (
                    <NotificationBadge count={pendingCount}>
                      {item.icon}
                    </NotificationBadge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, flexShrink: 0, bgcolor: 'background.paper' }}>
        <Avatar 
          src={user?.profilePicture || user?.employee?.profilePicture}
          sx={{ bgcolor: 'secondary.main', mr: 1 }}
        >
          {user?.employee?.firstName?.[0]}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.primary">
            {user?.employee?.firstName} {user?.employee?.lastName}
          </Typography>
          <Button color="secondary" size="small" onClick={logout} sx={{ mt: 0.5 }}>
            Logout
          </Button>
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
        elevation={1}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Back Office Management
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="sidebar navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      <FirstTimeLoginDialog
        open={showFirstTimeLogin}
        onPasswordChanged={handlePasswordChanged}
        userEmail={user?.email || ''}
        userRole={user?.role || ''}
      />
    </Box>
  )
}

export default Layout 